import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/store';
import { ElectronicSignature } from '~/components/SignatureVerification/ElectronicSignature/ElectronicSignature.tsx';
import { DocStatusEnum, TDocument } from '../../types.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import { infoMessages } from '~/constants/infoMessages.ts';
import api from '~/libs/api.ts';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader.tsx';
import useGetContragentTypes from '../../../../hooks/useGetContragentTypes.ts';
import {
  getSignatureEmail,
  validateBeforeSigning,
} from '~/store/identification-form/thunks/documentThunks.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { clearForm, setIdentificationError } from '~/store/identification-form/slices.ts';
import { useNavigate } from 'react-router';
import { clearDiiaState } from '~/store/diia/slices.ts';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { ERROR_MESSAGES } from '~/modules/identification/forms/Documents/constants.tsx';
import { getMessageValidBeforeSigning } from '~/modules/identification/forms/Documents/utils.ts';
import { useDocumentsQuery } from '~/queries/useDocumentsQuery.ts';
import * as Sentry from '@sentry/react';
import { useSignActions } from '../../hooks/useSignActions.ts';
import {
  ManagerVerificationStatus,
  TAssetManager,
} from '../../../ManagersAndRepresentatives/types.ts';
import { clearUsersState } from '~/store/users/slices.ts';
import { Box, Button, Typography } from '@mui/material';
import { styles } from '../../styles.ts';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal.tsx';
import {
  selectIdentification,
  selectManagersAndRepresentatives,
} from '~/store/identification-form/selectors.ts';
import { TimerHandle } from '~/components/Timer/Timer.tsx';

interface SignDocumentsProps {
  documentsToSign: TDocument[];
  storeDocuments: TDocument[];
  mainDocuments: TDocument[];
  additionalMainDocuments: TDocument[];
  applicationId: string;
  fetchPreview: (id: number | string) => void;
}

export const SignDocuments = React.memo(
  ({
    documentsToSign,
    storeDocuments,
    mainDocuments,
    additionalMainDocuments,
    applicationId,
    fetchPreview,
  }: SignDocumentsProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { webDocs } = useDocumentsQuery();
    const { isLegalResident, isSoleTrader } = useGetContragentTypes();
    const { isAssetManager, isClientLogin, isManager } = useGetUserRole();

    const { assetManagers } = useSelector(selectManagersAndRepresentatives) || {};

    const {
      error: identificationError,
      loading: identificationLoading,
      isReidentification,
    } = useSelector(selectIdentification) || {};

    const {
      loading: isDiiaLoading,
      error: diiaError,
      deepLink,
    } = useSelector((state: RootState) => state.diia) || {};

    const { isLoading: IITLoading, error: IITError } =
      useSelector((state: RootState) => state.iit) || {};

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOpenModalCongratulations, setIsOpenModalCongratulations] = useState(false);
    const [isModalCongratulationsText, setIsModalCongratulationsText] = useState<string | null>(
      null
    );
    const [getStatusPromise, setGetStatusPromise] = useState<{ abort: () => void } | null>(null);

    const timerRef = useRef<TimerHandle>(null);

    const isFirstSignAttemptCount = storeDocuments.every(
      (item) => item.docStatus === DocStatusEnum.NOT_VERIFIED
    );
    const isTemplateGeneration = useMemo(
      () => mainDocuments.some((doc) => doc.idDoc === null),
      [mainDocuments]
    );
    const isLoading = identificationLoading || isDiiaLoading || IITLoading;
    const error = identificationError || diiaError || IITError;
    const showSignButton = isClientLogin || isAssetManager;

    useEffect(() => {
      if (error) setIsModalOpen(false);
    }, [error]);

    const closeModal = useCallback(() => {
      setIsModalOpen(false);
      getStatusPromise?.abort();
      dispatch(clearDiiaState());
      setGetStatusPromise(null);
    }, []);

    const forceBpmRequest = useCallback(async () => {
      try {
        setIsModalOpen(false);
        setIsOpenModalCongratulations(true);
        if (isAssetManager) {
          setIsModalCongratulationsText(infoMessages.APPLICATION_DOCUMENTS_ACCEPTED);
        } else {
          setIsModalCongratulationsText(infoMessages.DOCUMENTS_SIGNED_AND_SENT);
        }

        if (isSoleTrader || isLegalResident) {
          await api.post(`/identification/force-bpm/order/application/${applicationId}`, null);
        }
      } catch (error) {
        Sentry.captureException(error);
        setIsModalOpen(false);
      }
    }, [isAssetManager, isSoleTrader, isLegalResident, applicationId, isReidentification]);

    const handleTimeUp = () => {
      timerRef.current?.resetTimer();
    };

    const { handleDiiaSignature, handleDiiaSignatureAssetManager, handleIITSignature, onIITSign } =
      useSignActions({
        applicationId,
        closeModal,
        documentsToSign,
        isFirstSignAttemptCount,
        forceBpmRequest,
        setGetStatusPromise,
        timerRef,
      });

    const handleValidateBeforeSigning = async () => {
      try {
        const result = await dispatch(
          validateBeforeSigning({ applicationId: applicationId! })
        ).unwrap();

        const { readyForSigning, missingForms, missingDocuments } = result;

        if (!readyForSigning) {
          const { forms, documents } = getMessageValidBeforeSigning(
            missingForms ?? [],
            missingDocuments ?? [],
            webDocs ?? []
          );
          dispatch(
            setIdentificationError({
              errorMessage: ERROR_MESSAGES.MISSING_FORMS_AND_DOCUMENTS(forms, documents),
            })
          );
        } else {
          setIsModalOpen(true);
        }
      } catch (error) {
        Sentry.captureException(error);
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    };

    const handleSignatureButton = () => {
      const isAssetManagerError =
        assetManagers &&
        assetManagers.length > 0 &&
        assetManagers.some((manager: TAssetManager) =>
          manager.isHead
            ? false
            : manager.verificationStatus !== ManagerVerificationStatus.COMPLETED
        );

      if (isAssetManagerError) {
        dispatch(
          setIdentificationError({ errorMessage: errorMessages.ASSET_MANAGER_VERIFICATION })
        );
        return;
      }

      if (!additionalMainDocuments || !Array.isArray(additionalMainDocuments)) {
        dispatch(
          setIdentificationError({ errorMessage: errorMessages.MISSING_DOCUMENTS_BEFORE_SIGNING })
        );
        return;
      }

      if (isTemplateGeneration) {
        dispatch(
          setIdentificationError({
            errorMessage: errorMessages.MISSING_DOCUMENTS_BEFORE_DOCUMENT_GENERATION,
          })
        );
        return;
      }

      const allDocsFilled = additionalMainDocuments.every((doc) => doc?.idDoc);

      if (!allDocsFilled) {
        dispatch(
          setIdentificationError({
            errorMessage: errorMessages.UNVERIFIED_DOCUMENTS_BEFORE_SIGNING,
          })
        );
        return;
      }

      if (!isAssetManager) {
        handleValidateBeforeSigning();
      }

      setIsModalOpen(true);
    };

    const handleSendingSignatureButton = async () => {
      try {
        if (!additionalMainDocuments || !Array.isArray(additionalMainDocuments)) {
          dispatch(
            setIdentificationError({
              errorMessage: errorMessages.MISSING_DOCUMENTS_BEFORE_SENDING_TO_CLIENT,
            })
          );
          return;
        }

        const allDocsFilled = additionalMainDocuments.every((doc) => doc?.idDoc);

        if (!allDocsFilled) {
          dispatch(
            setIdentificationError({
              errorMessage: errorMessages.MISSING_DOCUMENTS_BEFORE_SENDING_TO_CLIENT,
            })
          );
        } else {
          try {
            await dispatch(getSignatureEmail({ applicationId }));
            setIsOpenModalCongratulations(true);
            setIsModalCongratulationsText(infoMessages.APPLICATION_SENT_FOR_SIGNING);
          } catch (error) {
            Sentry.captureException(error);
            dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        dispatch(setIdentificationError({ errorMessage: errorMessages.SERVICE_UNAVAILABLE }));
      }
    };

    const closeModalCongratulations = () => {
      if (isAssetManager) {
        dispatch(clearUsersState());
      }
      setIsOpenModalCongratulations(false);
      dispatch(clearForm());
      navigate('/services');
    };

    return (
      <>
        {isLoading && <FullPageLoader />}
        <ElectronicSignature
          isModalOpen={isModalOpen}
          handleCloseModal={closeModal}
          handleSignatureDiia={
            isAssetManager ? handleDiiaSignatureAssetManager : handleDiiaSignature
          }
          handleSignatureIIT={handleIITSignature}
          deepLink={deepLink}
          onIITSign={onIITSign}
          handleTimeUp={handleTimeUp}
          handlePreview={fetchPreview}
          ref={timerRef}
          documents={documentsToSign}
          isDiiaSignatureDisabled={isLegalResident}
        />
        <BaseModal open={isOpenModalCongratulations} onClose={closeModalCongratulations}>
          <Box sx={styles.modal}>
            <Typography variant={'h4'} sx={styles.modalText}>
              Вітаємо!
            </Typography>
            <Typography variant={'body1'}>{isModalCongratulationsText}</Typography>
          </Box>
          <Box sx={styles.buttonBox}>
            <Button onClick={closeModalCongratulations} variant={'contained'} type="button">
              Підтвердити
            </Button>
          </Box>
        </BaseModal>
        {showSignButton && (
          <Box sx={styles.buttonBox}>
            <Button onClick={handleSignatureButton} variant={'contained'} type="button">
              Підписати КЕП
            </Button>
          </Box>
        )}
        {isManager && (
          <Box sx={styles.buttonBox}>
            <Button onClick={handleSendingSignatureButton} variant={'contained'} type="button">
              Відправити на підписання
            </Button>
          </Box>
        )}
      </>
    );
  }
);
